import { ApexCharts, ApexChartWidget } from './apex-chart-widget';
import { ChartDataXY, ChartSeriesXY } from '../data/chart-data-types';
import {
  currencyCodeToString,
  moneyToCompactStr,
  stringToCurrencyCode
} from '../../../../webmodule-common/other/currency-formatter';
import { customElement, property } from 'lit/decorators.js';
import { generatedPurchaseOrdersCache } from '../data/generated-purchase-orders-cache';
import { lang, tlang } from '../../../../webmodule-common/other/language/lang';
import { localDateTimeToServer } from '../../../../webmodule-common/other/datetime-converter';
import { NullPromise } from '../../../../webmodule-common/interop/webmodule-interop';
import { PurchaseOrderState } from '../../common/object-states';
import { ResultGetGeneratedPurchaseOrderRevenue } from '../../../api/supplier-api-interface-franchiseenetwork';

@customElement('generated-purchase-orders-revenue-widget')
export class GeneratedPurchaseOrdersWidget extends ApexChartWidget {
  @property({ type: Array }) branchIds?: string[] = [];
  @property() userId?: string;
  @property({ type: Array }) states: PurchaseOrderState[] = [
    PurchaseOrderState.Draft,
    PurchaseOrderState.IssuedPending,
    PurchaseOrderState.Issued,
    PurchaseOrderState.Completed
  ];

  private _cache = generatedPurchaseOrdersCache();

  // Track extra per-currency charts and connection state
  private charts: any[] = [];
  private _isDisconnected = false;

  constructor() {
    super('purchase-order-issued-revenue');
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._isDisconnected = true;
    try {
      this.chart?.destroy?.();
    } catch (e) {
      /* noop */
    }
    this.chart = null;
    for (const c of this.charts) {
      try {
        c?.destroy?.();
      } catch (e) {
        /* noop */
      }
    }
    this.charts = [];
  }

  protected async refreshData(): NullPromise<ResultGetGeneratedPurchaseOrderRevenue> {
    if (this.dateRange && this.branchIds) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      const result = await this._cache?.getData(startDate, endDate, this.states, this.branchIds, this.userId);
      if (result) {
        return result;
      }
    }
    return null;
  }

  protected override getTitle(): string {
    return tlang`!!purchase-order!! (Net Amount) - ${this.dateRange?.label}`;
  }

  protected getSeriesColorByState(state: PurchaseOrderState): string {
    // return the corresponding badge colour
    switch (state) {
      case PurchaseOrderState.Draft:
        return '#0ea5e9';
      case PurchaseOrderState.IssuedPending:
        return '#a855f7';
      case PurchaseOrderState.Issued:
        return '#eab308';
      case PurchaseOrderState.Completed:
        return '#52525b';
      default:
        return '#3282cc'; // base colour
    }
  }

  protected async updateData(): Promise<void> {
    const results = await this.refreshData();
    if (!results) return;

    await this.updateComplete;
    if (!this.isConnected || this._isDisconnected) return;

    // Clear any existing charts and container before rendering
    try {
      this.chart?.destroy?.();
    } catch (e) {
      /* noop */
    }
    this.chart = null;
    for (const c of this.charts) {
      try {
        c?.destroy?.();
      } catch (e) {
        /* noop */
      }
    }
    this.charts = [];

    const parent = this.querySelector('.purchase-order-issued-revenue');
    if (!parent || !this.isConnected || this._isDisconnected) return;
    parent.innerHTML = '';

    // Group by currency using enum->ISO mapping
    const currencyGroups: Record<string, typeof results.revenueSummary> = {} as any;
    for (const item of results.revenueSummary) {
      const currency = currencyCodeToString((item as any).currency) ?? 'USD';
      (currencyGroups[currency] ??= []).push(item as any);
    }

    // Two-phase render: build every currency container first (phase 1) so the
    // CSS grid settles to its final column count/width BEFORE any chart measures
    // itself (phase 2). Rendering a chart while later containers are still
    // missing makes ApexCharts bake in the full-card width, which then overflows
    // once the grid splits into columns (the "bleeding" overlap between
    // currencies).
    const pending: { container: HTMLDivElement; options: any }[] = [];
    Object.entries(currencyGroups).forEach(([currency, revenueSummary]) => {
      if (!this.isConnected || this._isDisconnected) return;

      const chartSeries: ChartSeriesXY[] = [];
      for (const purchaseOrderState of results.purchaseOrderStates) {
        const chartData: ChartDataXY[] = [];
        const data = revenueSummary.filter(d => d.state === purchaseOrderState || d.state === -1);
        data.forEach(r => chartData.push({ x: this.convertDateToMillis(r.date), y: r.dailyRevenue }));
        chartSeries.push({
          name: tlang`${getPurchaseOrderStatusStr(purchaseOrderState)}`,
          data: chartData,
          color: this.getSeriesColorByState(purchaseOrderState)
        });
      }

      const chartOptions = {
        chart: {
          type: 'area',
          height: '179px',
          selection: { enabled: false },
          stacked: false,
          toolbar: {
            tools: {
              download: false,
              customIcons: [
                {
                  icon: '<i class="fa fa-plus-circle fa-lg"></i>',
                  index: 0,
                  title: 'Zoom',
                  class: 'custom-icon',
                  click: (chart: any, opts: any, e: any) => this.onChartClick(e, chart, opts)
                }
              ]
            }
          },
          zoom: { enabled: false }
        },
        stroke: { width: 1 },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false },
        states: {
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } }
        },
        series: [],
        xaxis: { type: 'datetime' },
        yaxis: {
          labels: { formatter: (value: number) => `${moneyToCompactStr(value, stringToCurrencyCode(currency))}` }
        },
        noData: { text: 'Loading...' }
      } as any;

      const parentEl = this.querySelector('.purchase-order-issued-revenue');
      if (!parentEl || !this.isConnected || this._isDisconnected) return;
      const container = document.createElement('div');
      container.className = 'purchase-order-issued-revenue-currency';
      const caption = document.createElement('div');
      caption.textContent = `${this.getTitle()} (${currency})`;
      caption.style.fontWeight = '600';
      caption.style.margin = '8px 0';
      container.appendChild(caption);
      parentEl.appendChild(container);

      pending.push({ container, options: { ...chartOptions, series: chartSeries } });
    });

    // Phase 2: every container is now in the grid at its final width, so each
    // chart measures its own column instead of the whole card.
    if (!this.isConnected || this._isDisconnected) return;
    for (const { container, options } of pending) {
      const chart = new ApexCharts(container, options);
      chart.render();
      this.charts.push(chart);
    }
  }
}

function getPurchaseOrderStatusStr(state: PurchaseOrderState): string {
  const stateToString = () => {
    switch (state) {
      case PurchaseOrderState.Completed:
        return 'completed';
      case PurchaseOrderState.Issued:
        return 'issued';
      case PurchaseOrderState.IssuedPending:
        return 'issuedpending';
      case PurchaseOrderState.Draft:
        return 'draft';
      default:
        return 'none';
    }
  };

  const text = `%%purchase-order-state-${stateToString()}%%`;
  return lang(text);
}
